import { useFeature } from '@treams/treams-hooks/useFeature';
import * as graphql from '@treams/treams-network';

import useDetectPrint from '../hooks/useDetectPrint';
import EmptyAnswerDisplay from './display/EmptyAnswerDisplay';
import IrrelevantAnswerDisplay from './display/IrrelevantAnswerDisplay';
import PrivateAnswerDisplay from './display/PrivateAnswerDisplay';
import UnsharedAnswerDisplay from './display/UnsharedAnswerDisplay';
import EvaluationAnswerComposer from './EvaluationAnswerComposer';
import EvaluationAnswerDisplay from './EvaluationAnswerDisplay';
import EvaluationAnswerWrapper from './EvaluationAnswerWrapper';
import PostShareAnswerEdits, { PostShareAnswerRevision } from './PostShareAnswerEdits';

const isEmptyEvaluationSubmissionItem = (
	item: graphql.EvaluationSubmissionItem,
	multipleChoiceEnabled: boolean,
) => {
	if (item.type === graphql.EvaluationSectionItemType.OpenQuestion) {
		return (
			!item.openQuestion ||
			item.openQuestion.ops
				.map((op: { insert?: string }) => op.insert ?? '')
				.join('')
				.trim().length === 0
		);
	}

	if (item.type === graphql.EvaluationSectionItemType.Sentiment) {
		return item.sentiment === null || item.sentiment === undefined;
	}

	if (item.type === graphql.EvaluationSectionItemType.Stars) {
		return item.stars === null || item.stars === undefined;
	}

	if (item.type === graphql.EvaluationSectionItemType.Competency) {
		return item.competency === null || item.competency === undefined;
	}

	if (item.type === graphql.EvaluationSectionItemType.ReviewGoals) {
		return item.reviewGoals === null || item.reviewGoals === undefined;
	}

	if (multipleChoiceEnabled && item.type === graphql.EvaluationSectionItemType.MultipleChoice) {
		return !item.multipleChoice || item.multipleChoice.length === 0;
	}

	return false;
};

export default function EvaluationSubmissionAnswer(props: {
	sectionIndex: number;
	itemIndex: number;
	goal?: graphql.EvaluationSubmissionGoal;
	submission: graphql.EvaluationSubmission;
	party: graphql.EvaluationSubmissionParty;
	onSave: () => void;
	isSaving: boolean;
}) {
	const isPrinting = useDetectPrint();
	const multipleChoiceEnabled = useFeature(graphql.FeatureName.ConversationMultipleChoice);
	const sectionItem =
		props.submission.evaluationForm.sections[props.sectionIndex].items[props.itemIndex]!;

	const partyResult = props.submission[props.party.toLowerCase() as 'employee' | 'manager'];
	const partyResultItem = partyResult.sections[props.sectionIndex].items[props.itemIndex]!;
	const isEmployee = props.party === graphql.EvaluationSubmissionParty.Employee;
	const revisions =
		(
			partyResultItem as graphql.EvaluationSubmissionItem & {
				revisions?: PostShareAnswerRevision[] | null;
			}
		).revisions ?? [];
	const edited =
		revisions.length > 0 ? (
			<PostShareAnswerEdits revisions={revisions} sectionItem={sectionItem} />
		) : null;

	if (
		(sectionItem.fillInBy === graphql.EvaluationFillInBy.Manager && isEmployee) ||
		(sectionItem.fillInBy === graphql.EvaluationFillInBy.Employee && !isEmployee)
	) {
		return (
			<EvaluationAnswerWrapper
				user={partyResult.user}
				side={isEmployee ? 'left' : 'right'}
				withAvatar
				variant={props.goal ? 'goalReflection' : 'irrelevant'}
			>
				<IrrelevantAnswerDisplay result={partyResult} />
			</EvaluationAnswerWrapper>
		);
	}

	if (partyResult.viewerCanUpdate && !isPrinting) {
		return (
			<EvaluationAnswerWrapper
				user={partyResult.user}
				side={isEmployee ? 'left' : 'right'}
				withAvatar
				variant={props.goal ? 'goalReflection' : 'default'}
			>
				<EvaluationAnswerComposer
					submission={props.submission}
					section={props.sectionIndex}
					item={props.itemIndex}
					goal={props.goal}
					result={partyResult}
					onSave={props.onSave}
					isSaving={props.isSaving}
					autosave={!partyResult.hasShared}
				/>
				{edited}
			</EvaluationAnswerWrapper>
		);
	}

	if (partyResult.hasShared && !partyResultItem.viewerCanView) {
		return (
			<EvaluationAnswerWrapper
				user={partyResult.user}
				side={isEmployee ? 'left' : 'right'}
				variant={props.goal ? 'goalReflection' : 'empty'}
				withAvatar
			>
				<PrivateAnswerDisplay result={partyResult} />
			</EvaluationAnswerWrapper>
		);
	}

	if (!partyResult.hasShared && !partyResultItem.viewerCanView) {
		return (
			<EvaluationAnswerWrapper
				user={partyResult.user}
				side={isEmployee ? 'left' : 'right'}
				variant={props.goal ? 'goalReflection' : 'empty'}
				withAvatar
			>
				<UnsharedAnswerDisplay result={partyResult} />
			</EvaluationAnswerWrapper>
		);
	}

	if (isEmptyEvaluationSubmissionItem(partyResultItem, multipleChoiceEnabled) && !edited) {
		return (
			<EvaluationAnswerWrapper
				user={partyResult.user}
				side={isEmployee ? 'left' : 'right'}
				withAvatar
				variant={props.goal ? 'goalReflection' : 'empty'}
			>
				<EmptyAnswerDisplay result={partyResult} />
			</EvaluationAnswerWrapper>
		);
	}

	if (isEmptyEvaluationSubmissionItem(partyResultItem, multipleChoiceEnabled)) {
		return (
			<EvaluationAnswerWrapper
				user={partyResult.user}
				side={isEmployee ? 'left' : 'right'}
				withAvatar
				variant={props.goal ? 'goalReflection' : 'default'}
			>
				<EmptyAnswerDisplay result={partyResult} />
				{edited}
			</EvaluationAnswerWrapper>
		);
	}

	return (
		<EvaluationAnswerWrapper
			user={partyResult.user}
			side={isEmployee ? 'left' : 'right'}
			withAvatar
			variant={props.goal ? 'goalReflection' : 'default'}
		>
			<EvaluationAnswerDisplay
				resultItem={partyResultItem}
				sectionItem={sectionItem}
				party={props.party}
				submission={props.submission}
				sectionIndex={props.sectionIndex}
				itemIndex={props.itemIndex}
				goal={props.goal}
			/>
			{edited}
		</EvaluationAnswerWrapper>
	);
}
